/**
 * Klient k API + offline fronta odpovedi.
 *
 * Princip offline rezimu:
 *   - fronta ke zkouseni se stahne dopredu a ulozi do localStorage
 *   - odpovedi se VZDY nejdriv zapisou lokalne a az potom zkusi odeslat
 *   - kazda odpoved ma `clientId` (UUID) -> server deduplikuje, takze
 *     opakovane odeslani po vypadku site nic nepokazi
 */

import type {
  AnswerInput,
  AnswerResult,
  AuthUser,
  Concept,
  ConceptPrefs,
  Deck,
  Example,
  ExtractCaptureResult,
  ExtractCandidate,
  ExtractPreview,
  HealthInfo,
  Lang,
  ReviewQueue,
  Stats,
  UserSettings,
} from '@lexio/shared';

const QUEUE_KEY = 'lexio.queue.v2';
const PENDING_KEY = 'lexio.pending.v2';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let code = 'error';
    let message = `Požadavek selhal (${res.status})`;
    try {
      const body = await res.json();
      code = body.error ?? code;
      message = body.message ?? message;
    } catch {
      // odpoved neni JSON - necháme obecnou zprávu
    }
    throw new ApiError(res.status, code, message);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

// --- Auth ---

export const login = (email: string, password: string) =>
  request<{ user: AuthUser; token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const logout = () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });

export const me = () => request<{ user: AuthUser }>('/api/auth/me');

export const getHealth = () => request<HealthInfo>('/api/health');

// --- Fronta ke zkouseni ---

interface CachedQueue {
  queue: ReviewQueue;
  cachedAt: number;
}

export function readCachedQueue(): ReviewQueue | null {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as CachedQueue).queue;
  } catch {
    return null;
  }
}

function writeCachedQueue(queue: ReviewQueue): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify({ queue, cachedAt: Date.now() } satisfies CachedQueue));
  } catch {
    // Plne uloziste neni duvod k padu appky - jen prijdeme o offline rezim.
  }
}

/**
 * Stahne frontu ze serveru a uschova ji pro offline. Pri vypadku site vrati posledni znamou.
 * `extraNew` prida nove karty nad denni limit ("chci se ucit dal").
 */
export async function fetchQueue(extraNew = 0): Promise<{ queue: ReviewQueue; offline: boolean }> {
  try {
    const queue = await request<ReviewQueue>(`/api/review/queue${extraNew > 0 ? `?extraNew=${extraNew}` : ''}`);
    writeCachedQueue(queue);
    return { queue, offline: false };
  } catch (err) {
    // 401 znamena odhlaseni - to musi probublat nahoru, ne se schovat za offline.
    if (err instanceof ApiError && err.status === 401) throw err;

    const cached = readCachedQueue();
    if (cached) return { queue: cached, offline: true };
    throw err;
  }
}

// --- Odpovedi s offline frontou ---

export function readPending(): AnswerInput[] {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) ?? '[]') as AnswerInput[];
  } catch {
    return [];
  }
}

function writePending(items: AnswerInput[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(items));
  } catch {
    // ignorujeme - viz vyse
  }
}

/**
 * Zaznamena odpoved. Nejdriv lokalne, potom se pokusi odeslat celou frontu.
 * Nikdy nevyhodi vyjimku kvuli siti - dril nesmi zamrznout kvuli vypadku.
 */
export async function recordAnswer(answer: AnswerInput): Promise<{ synced: boolean; pending: number }> {
  writePending([...readPending(), answer]);
  return flushPending();
}

/** Odesle vse, co ceka ve fronte. Uspesne odeslane polozky z fronty odstrani. */
export async function flushPending(): Promise<{ synced: boolean; pending: number }> {
  const pending = readPending();
  if (pending.length === 0) return { synced: true, pending: 0 };

  const batch = pending.slice(0, 500);

  try {
    const { results } = await request<{ results: AnswerResult[] }>('/api/review/answers', {
      method: 'POST',
      body: JSON.stringify({ answers: batch }),
    });

    const done = new Set(results.map((r) => r.clientId));
    const rest = readPending().filter((a) => !done.has(a.clientId));
    writePending(rest);
    return { synced: rest.length === 0, pending: rest.length };
  } catch (err) {
    // Karta mezitim zmizela (napr. po zmene obsahu) - jinak by frontu blokovala navzdy.
    if (err instanceof ApiError && err.status === 404) {
      writePending(readPending().slice(batch.length));
      return { synced: false, pending: readPending().length };
    }
    return { synced: false, pending: pending.length };
  }
}

// --- Prioritizace ---

export const setPrefs = (conceptId: string, patch: Partial<ConceptPrefs>) =>
  request<{ prefs: ConceptPrefs }>(`/api/concepts/${conceptId}/prefs`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

// --- Prohlizec slovni zasoby ---

export type ConceptWithPrefs = Concept & { prefs: ConceptPrefs };

export interface ConceptListParams {
  q?: string;
  topic?: string;
  deck?: string;
  level?: string;
  lang?: Lang;
  filter?: 'all' | 'known' | 'suppressed' | 'prioritized' | 'unseen';
  limit?: number;
  offset?: number;
}

export function listConcepts(params: ConceptListParams) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  return request<{ items: ConceptWithPrefs[]; total: number; limit: number; offset: number }>(
    `/api/concepts?${search.toString()}`,
  );
}

/** Uprava ze slovniku. Prazdny `term` jazyk odebere; `examples` nahradi vsechny priklady. */
export interface ConceptEdit {
  terms: Partial<Record<Lang, { term: string; variants: string[]; note: string | null }>>;
  examples?: Example[];
}

export const updateConcept = (conceptId: string, edit: ConceptEdit) =>
  request<{ concept: Concept }>(`/api/concepts/${conceptId}`, {
    method: 'PUT',
    body: JSON.stringify(edit),
  });

export const listTopics = () =>
  request<{ topics: { slug: string; name: string; conceptCount: number }[] }>('/api/topics');

/** Jazyky s obsahem + kolik konceptu umi kazdy smer (aby se nedal zapnout prazdny). */
export const listLangs = () =>
  request<{
    counts: Partial<Record<Lang, number>>;
    pairs: { direction: string; conceptCount: number }[];
  }>('/api/langs');

export const listDecks = () => request<{ decks: Deck[] }>('/api/decks');

export const setDeckActive = (slug: string, active: boolean) =>
  request<{ slug: string; active: boolean }>(`/api/decks/${slug}`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });

// --- Nastaveni a statistiky ---

export const getSettings = () => request<{ settings: UserSettings }>('/api/settings');

export const patchSettings = (patch: Partial<UserSettings>) =>
  request<{ settings: UserSettings }>('/api/settings', { method: 'PATCH', body: JSON.stringify(patch) });

export const getStats = () => request<Stats>('/api/stats');

export const translate = (text: string, from?: 'cs' | 'en', to?: 'cs' | 'en') =>
  request<{
    text: string;
    /** Prazdne, kdyz prekladac nefungoval, ale vyraz uz je ve slovniku. */
    translated: string;
    from: 'cs' | 'en';
    to: 'cs' | 'en';
    /** Jistota prekladace 0-1; pod 0.5 byva vysledek nesmysl (preklep, neznamy vyraz). */
    match: number;
    alternatives: string[];
    /** Jen z AI: kratka poznamka k pouziti (slang, vulgarni, past). */
    note: string;
    /** Koncepty, ktere uz ten vyraz maji. */
    existing: Concept[];
  }>('/api/translate', {
    method: 'POST',
    body: JSON.stringify({ text, from, to }),
  });

export const captureConcept = (body: {
  sourceLang: 'cs' | 'en';
  targetLang: 'cs' | 'en';
  sourceTerm: string;
  targetTerm: string;
  context?: string;
  contextTranslated?: string;
  targetVariants?: string[];
  targetNote?: string;
}) =>
  request<{ concept: Concept; alreadyExisted: boolean }>('/api/concepts/capture', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const previewExtract = (body: {
  text: string;
  sourceLang: 'cs' | 'en';
  targetLang: 'cs' | 'en';
  offset?: number;
  translateContext?: boolean;
}) =>
  request<ExtractPreview>('/api/extract/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Po kolika polozkach posilat ulozeni - server jich bere vic, ale velke davky se hur opakuji. */
const CAPTURE_BATCH = 40;

/** Ulozi vytezena slova; dlouhy seznam posle po castech a vysledky secte. */
export async function captureExtract(body: {
  sourceLang: 'cs' | 'en';
  targetLang: 'cs' | 'en';
  items: Omit<ExtractCandidate, 'alreadyHave'>[];
}): Promise<ExtractCaptureResult> {
  const total: ExtractCaptureResult = { added: 0, existed: 0, failed: 0 };
  for (let i = 0; i < body.items.length; i += CAPTURE_BATCH) {
    const res = await request<ExtractCaptureResult>('/api/extract/capture', {
      method: 'POST',
      body: JSON.stringify({ ...body, items: body.items.slice(i, i + CAPTURE_BATCH) }),
    });
    total.added += res.added;
    total.existed += res.existed;
    total.failed += res.failed;
  }
  return total;
}
